/**
 * The only component in the app that renders currency.
 *
 * `cents` is an integer in the currency's minor unit, as produced by
 * `parseMoney` at the API boundary — never a float. The /100 below is the
 * inverse of the ×100 there, so the pair round-trips for zero-decimal
 * currencies too: a ¥1,250 balance arrives as "1250", becomes 125000, and
 * comes back out as ¥1,250.
 */

type Props = {
  cents: number
  currency?: string
  locale?: string
  variant?: 'inline' | 'hero'
  /** 'stock' — a balance or total, always plain ink. 'flow' — colours by sign. */
  kind?: 'stock' | 'flow'
  /**
   * A figure that sits quietly beside a label (a day band's subtotal): regular
   * weight, and it inherits its colour instead of setting `--ink` or `--pos`. Inline
   * styles beat any stylesheet, so this cannot be done from CSS.
   */
  muted?: boolean
  /**
   * 'full' (default) — the minor unit shown. 'whole' — rounded to the major unit,
   * for a chart label ("−$360"). 'compact' — `+$6.5K`, for a chart axis. Display
   * rounding only: the cents passed in are never altered or summed.
   */
  display?: 'full' | 'whole' | 'compact'
  /**
   * A stock's colour with a flow's sign: `+` on a gain, `−` on a loss, and NO
   * colour of its own, so it inherits. For a net figure on a `HeroTile`, where
   * a flow's inline `--pos` / `--ink` would beat the tile's `--on-accent`
   * (`--ink` on lime is 1.12:1 in dark). Ignored by `kind="flow"`, which
   * already signs.
   */
  signed?: boolean
  /**
   * Default `true`. `false` drops the currency symbol and the whitespace beside
   * it, for a figure grid whose caller names the currency once, elsewhere (M8
   * Reports, B11). Sign, grouping and the currency's own fraction digits are
   * kept, which is why this filters the parts instead of switching to
   * `style: 'decimal'` (JPY has no minor unit). Every figure still goes
   * through `Money`, so rule 4 holds.
   */
  symbol?: boolean
}

/** U+2212 MINUS SIGN — the typographic minus, not a hyphen. */
const MINUS = '\u2212'

/**
 * Constructing an Intl.NumberFormat is the expensive part; formatting is
 * cheap. Money goes into DataGrid rows in Task 16, where a 500-row transaction
 * list would otherwise build 500 formatters on every render.
 */
const FORMATTERS = new Map<string, Intl.NumberFormat>()

const DISPLAY_OPTIONS: Record<NonNullable<Props['display']>, Intl.NumberFormatOptions> = {
  full: {},
  whole: { maximumFractionDigits: 0, minimumFractionDigits: 0 },
  compact: { notation: 'compact', maximumFractionDigits: 1, minimumFractionDigits: 0 },
}

function formatter(locale: string, currency: string, display: NonNullable<Props['display']>): Intl.NumberFormat {
  const key = `${locale}|${currency}|${display}`
  const cached = FORMATTERS.get(key)
  if (cached) return cached
  const made = new Intl.NumberFormat(locale, { style: 'currency', currency, ...DISPLAY_OPTIONS[display] })
  FORMATTERS.set(key, made)
  return made
}

/** Drop currency parts, and the whitespace-only literal sitting next to one. */
function withoutSymbol(parts: Intl.NumberFormatPart[]): Intl.NumberFormatPart[] {
  return parts.filter((p, i) => {
    if (p.type === 'currency') return false
    if (p.type === 'literal' && /^\s+$/.test(p.value)) {
      return parts[i - 1]?.type !== 'currency' && parts[i + 1]?.type !== 'currency'
    }
    return true
  })
}

export function Money({
  cents,
  currency = 'CAD',
  locale = 'en-CA',
  variant = 'inline',
  kind = 'stock',
  muted = false,
  display = 'full',
  signed = false,
  symbol = true,
}: Props) {
  // Loud rather than rendering "$NaN" or "$0.02". parseMoney and formatCents
  // both throw on anything that is not a safe integer, and this is the third
  // member of that trio — a float or NaN arriving here means the integer-cents
  // contract broke upstream, which is worth a named error at the boundary
  // rather than a plausible-looking number on screen.
  if (!Number.isSafeInteger(cents)) {
    throw new Error(`Money: expected safe integer cents, got ${JSON.stringify(cents)}`)
  }

  const allParts = formatter(locale, currency, display).formatToParts(Math.abs(cents) / 100)
  const parts = symbol ? allParts : withoutSymbol(allParts)

  // Split around the fraction rather than assuming symbol-first: several of
  // the 30 currencies place the symbol after the amount, and the locale
  // decides. Three pieces, not two — `after` is what follows the fraction,
  // such as de-DE's " €", and it belongs at full size next to the number
  // rather than shrunk into the muted cents.
  const fracIndex = parts.findIndex((p) => p.type === 'fraction')
  const hasFrac = fracIndex !== -1
  const head = parts
    .slice(0, hasFrac ? fracIndex - 1 : parts.length)
    .map((p) => p.value)
    .join('')
  // The decimal separator sits immediately before the fraction, and travels
  // with it — ".40", not "40".
  const frac = hasFrac
    ? parts
        .slice(fracIndex - 1, fracIndex + 1)
        .map((p) => p.value)
        .join('')
    : ''
  const after = hasFrac
    ? parts
        .slice(fracIndex + 1)
        .map((p) => p.value)
        .join('')
    : ''

  // Sign is not the same question as colour. M0-R24 governs colour — a stock
  // is plain ink however positive — but a negative stock is still negative,
  // and rendering a -$118.44 card balance as "$118.44" loses the difference
  // between owing and holding. Only the leading "+" is flow-specific.
  const sign = cents < 0 ? MINUS : (kind === 'flow' || signed) && cents > 0 ? '+' : ''
  // A STOCK sets no colour of its own and inherits — issue #395, B1.
  // `--ink` as an INLINE style beats `.hero-tile`'s `color: var(--on-accent)`
  // (`surfaces.css:97`) outright, which is why a hero total on the lime tile
  // measured 1.12:1 dark / 14.83:1 light (`--ink` on `--accent`, remeasured
  // independently with `test/contrast.ts`'s own `contrastRatio`, matching
  // this report's table) — inheriting instead gives `--on-accent` on
  // `--accent`, measured at **12.65:1 dark / 12.05:1 light**
  // (`tokens.css:143-144` states the identical figure for the same pairing,
  // used there for the focus ring on a lime tile — NOT the `10.79/11.35`
  // figure two paragraphs below at `tokens.css:166`, which is `--on-accent`
  // against `--accent-hover`, a different fill). Every OTHER stock call
  // site inherits `--ink` regardless, because `body { color: var(--ink) }`
  // (`reset.css:15`) or a `--control-ink`-bearing ancestor (`ListRow`'s own
  // row, `listRow.css:30`) already supplies it outside a `HeroTile` — checked
  // against every non-hero `kind="stock"`/`kind="flow"` call site in the repo
  // (`DataGrid.tsx`, `MerchantTransactions.tsx`, `registry.tsx`,
  // `ManualAccountForm.tsx`, `Accounts.tsx`), not merely assumed.
  //
  // A FLOW keeps an explicit colour for BOTH branches, unchanged from before
  // this fix — not only the positive one. `DataGrid.tsx`'s subtotal row
  // renders `kind="flow"` inside `.datagrid__group`, which sets its own
  // `color: var(--ink2)` (`dataGrid.css:122`); inheriting there would silently
  // retint every zero/negative subtotal from `--ink` to `--ink2`. Semantic
  // colour (`--pos`) for a real gain, explicit `--ink` otherwise, on both
  // variants, is the byte-identical behaviour this call site already had.
  const color = kind === 'flow' ? (cents > 0 ? 'var(--pos)' : 'var(--ink)') : undefined

  if (variant === 'hero') {
    return (
      <span style={{ color, letterSpacing: '-.03em', fontWeight: 600, whiteSpace: 'nowrap' }}>
        <span data-money="whole" style={{ fontSize: 'var(--type-hero)' }}>{`${sign}${head}`}</span>
        {frac && (
          // A literal `--ink2` was illegible on the lime tile — the fraction
          // needs to be DIMMER than whatever the whole number just inherited,
          // which a fixed value cannot be once the whole number's own colour
          // depends on context. `--control-ink-dim` resolves to `--on-accent-2`
          // inside a `HeroTile` (`tokens.css:882`) and `--ink3` elsewhere
          // (`tokens.css:475`) — the same surface-relative dimming
          // `ListRow`'s disabled state already reads off the identical triple.
          // Measured (composited over `--accent`, since `--on-accent-2` is
          // translucent): **5.43:1 dark / 5.74:1 light**, comfortably past
          // both the 4.5 body floor and M0-R4's 4.2 dim-rung floor.
          // `variant="hero"` has three call sites in the repo today,
          // verified by grep rather than carried forward — `registry.tsx
          // :494`, `AccountsSummary.tsx:195`, `AccountHero.tsx:168` (M5
          // Ticket 4's own new one) — and all three already sit inside a
          // `HeroTile`, so there is still no "elsewhere" case shipping yet
          // to regress. (This comment previously said "exactly two"; #425
          // T4's gate caught that the count had drifted.)
          <span data-money="frac" style={{ fontSize: 'var(--type-hero-frac)', color: 'var(--control-ink-dim)' }}>
            {frac}
          </span>
        )}
        {after && <span style={{ fontSize: 'var(--type-hero)' }}>{after}</span>}
      </span>
    )
  }

  // `nowrap`: an amount is one token. In a narrow grid cell the browser broke
  // `−$118.44` after the minus, leaving the sign orphaned on its own line —
  // measured at 320px and 1440px on the states page, two client rects for one
  // figure. CLAUDE.md says colour is never the only signal for over/under, and
  // a sign the eye reads as belonging to the line above is the signal lost.
  return (
    <span style={{ color: muted ? undefined : color, fontWeight: muted ? 400 : 600, whiteSpace: 'nowrap' }}>
      {`${sign}${head}${frac}${after}`}
    </span>
  )
}
